import React, { useState, useEffect, useRef, useCallback } from "react";
import { Typography, Button, Progress } from "antd";
import {
  X,
  ChevronLeft,
  ChevronRight,
  Play,
  Pause,
  Maximize2,
  SkipForward,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const { Text } = Typography;

/**
 * PreviewPlayer — A fullscreen-like slideshow modal that shows how
 * playlist content will play on the real display screen.
 *
 * Props:
 *  - visible (bool)
 *  - onClose (func)
 *  - playlistItems (array)
 *  - selectedRatio ({ label, value, description })
 */
const PreviewPlayer = ({
  visible,
  onClose,
  playlistItems = [],
  selectedRatio,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef(null);
  const progressRef = useRef(null);
  const videoRef = useRef(null);

  const currentItem = playlistItems[currentIndex] || null;

  // Duration per item in ms
  const getItemDuration = useCallback((item) => {
    if (!item) return 5000;
    const sec = item.durationSeconds || parseInt(item.duration) || 10;
    return sec * 1000;
  }, []);

  // Go to next item
  const goNext = useCallback(() => {
    setProgress(0);
    setCurrentIndex((prev) => (prev + 1) % playlistItems.length);
  }, [playlistItems.length]);

  // Go to previous item
  const goPrev = useCallback(() => {
    setProgress(0);
    setCurrentIndex((prev) =>
      prev === 0 ? playlistItems.length - 1 : prev - 1,
    );
  }, [playlistItems.length]);

  // Auto-advance timer — applies to both images AND videos
  useEffect(() => {
    if (!visible || !isPlaying || !currentItem || playlistItems.length === 0)
      return;

    const duration = getItemDuration(currentItem);
    const interval = 50;
    let elapsed = 0;

    progressRef.current = setInterval(() => {
      elapsed += interval;
      setProgress(Math.min((elapsed / duration) * 100, 100));
    }, interval);

    timerRef.current = setTimeout(() => {
      goNext();
    }, duration);

    return () => {
      clearTimeout(timerRef.current);
      clearInterval(progressRef.current);
    };
  }, [
    visible,
    isPlaying,
    currentIndex,
    currentItem,
    playlistItems.length,
    getItemDuration,
    goNext,
  ]);

  // Reset on open
  useEffect(() => {
    if (visible) {
      setCurrentIndex(0);
      setIsPlaying(true);
      setProgress(0);
    } else {
      clearTimeout(timerRef.current);
      clearInterval(progressRef.current);
    }
  }, [visible]);

  // Pause/resume
  const togglePlay = () => {
    setIsPlaying((prev) => !prev);
    if (videoRef.current) {
      if (isPlaying) videoRef.current.pause();
      else videoRef.current.play();
    }
  };

  // Keyboard controls
  useEffect(() => {
    if (!visible) return;
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight" || e.key === " ") goNext();
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "p") togglePlay();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [visible, goNext, goPrev]);

  if (!visible) return null;

  // Calculate preview dimensions from ratio
  const getPreviewStyle = () => {
    const [w, h] = (selectedRatio?.value || "16/9").split("/").map(Number);
    const aspect = w / h;
    if (aspect >= 1) {
      return { width: "80%", maxWidth: "900px", aspectRatio: `${w}/${h}` };
    } else {
      return { height: "80vh", maxHeight: "700px", aspectRatio: `${w}/${h}` };
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 10000,
        background: "rgba(0, 0, 0, 0.92)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* ── Top Bar ── */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          padding: "16px 24px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          background:
            "linear-gradient(180deg, rgba(0,0,0,0.6) 0%, transparent 100%)",
          zIndex: 10,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <div
            style={{
              padding: "4px 12px",
              borderRadius: "8px",
              background: "rgba(99, 102, 241, 0.3)",
              border: "1px solid rgba(99, 102, 241, 0.4)",
            }}
          >
            <Text
              style={{
                color: "#c7d2fe",
                fontSize: "12px",
                fontWeight: 600,
                letterSpacing: "0.05em",
              }}
            >
              PREVIEW • {selectedRatio?.label || "16:9"}
            </Text>
          </div>
          <Text style={{ color: "rgba(255,255,255,0.7)", fontSize: "13px" }}>
            {currentIndex + 1} / {playlistItems.length} •{" "}
            {currentItem?.name || "—"}
          </Text>
        </div>

        <Button
          type="text"
          icon={<X size={20} color="#ffffff" />}
          onClick={onClose}
          style={{
            border: "1px solid rgba(255,255,255,0.2)",
            borderRadius: "8px",
            background: "rgba(255,255,255,0.1)",
          }}
        />
      </div>

      {/* ── Content Area ── */}
      <div
        style={{
          ...getPreviewStyle(),
          background: "#111827",
          borderRadius: "12px",
          overflow: "hidden",
          position: "relative",
          boxShadow:
            "0 0 60px rgba(99, 102, 241, 0.15), 0 0 120px rgba(0, 0, 0, 0.5)",
          border: "2px solid rgba(255,255,255,0.08)",
        }}
      >
        <AnimatePresence mode="wait">
          {currentItem && (
            <motion.div
              key={currentItem.id + "-" + currentIndex}
              initial={{ opacity: 0, scale: 1.02 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.5, ease: "easeInOut" }}
              style={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "absolute",
                inset: 0,
              }}
            >
              {currentItem.type === "video" ? (
                <video
                  ref={videoRef}
                  src={currentItem.url || currentItem.previewUrl}
                  autoPlay
                  muted
                  loop
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                <img
                  src={currentItem.url || currentItem.previewUrl}
                  alt={currentItem.name}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Progress bar at bottom of screen */}
        <div
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: "3px",
            background: "rgba(255,255,255,0.1)",
          }}
        >
          <motion.div
            style={{
              height: "100%",
              background: "linear-gradient(90deg, #6366f1, #8b5cf6)",
              width: `${progress}%`,
            }}
            transition={{ duration: 0.05 }}
          />
        </div>
      </div>

      {/* ── Bottom Controls ── */}
      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          padding: "20px 24px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: "16px",
          background:
            "linear-gradient(0deg, rgba(0,0,0,0.6) 0%, transparent 100%)",
        }}
      >
        <Button
          type="text"
          icon={<ChevronLeft size={20} color="#ffffff" />}
          onClick={goPrev}
          style={{
            borderRadius: "50%",
            width: "40px",
            height: "40px",
            background: "rgba(255,255,255,0.1)",
            border: "1px solid rgba(255,255,255,0.15)",
          }}
        />

        <Button
          type="text"
          icon={
            isPlaying ? (
              <Pause size={22} color="#ffffff" />
            ) : (
              <Play size={22} color="#ffffff" />
            )
          }
          onClick={togglePlay}
          style={{
            borderRadius: "50%",
            width: "52px",
            height: "52px",
            background: "rgba(99, 102, 241, 0.5)",
            border: "1px solid rgba(99, 102, 241, 0.6)",
          }}
        />

        <Button
          type="text"
          icon={<ChevronRight size={20} color="#ffffff" />}
          onClick={goNext}
          style={{
            borderRadius: "50%",
            width: "40px",
            height: "40px",
            background: "rgba(255,255,255,0.1)",
            border: "1px solid rgba(255,255,255,0.15)",
          }}
        />
      </div>

      {/* ── Playlist Thumbnails ── */}
      <div
        style={{
          position: "absolute",
          bottom: "80px",
          left: "50%",
          transform: "translateX(-50%)",
          display: "flex",
          gap: "8px",
          padding: "8px",
          borderRadius: "12px",
          background: "rgba(0,0,0,0.5)",
          backdropFilter: "blur(8px)",
          border: "1px solid rgba(255,255,255,0.08)",
          maxWidth: "90vw",
          overflowX: "auto",
        }}
      >
        {playlistItems.map((item, idx) => (
          <div
            key={item.id}
            onClick={() => {
              setCurrentIndex(idx);
              setProgress(0);
            }}
            style={{
              width: "56px",
              height: "40px",
              borderRadius: "6px",
              overflow: "hidden",
              cursor: "pointer",
              border:
                idx === currentIndex
                  ? "2px solid #6366f1"
                  : "2px solid transparent",
              opacity: idx === currentIndex ? 1 : 0.5,
              transition: "all 0.2s ease",
              flexShrink: 0,
            }}
          >
            {item.previewUrl ? (
              <img
                src={item.previewUrl}
                alt=""
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : (
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  background: "#334155",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Text style={{ fontSize: "8px", color: "#94a3b8" }}>
                  {idx + 1}
                </Text>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Keyboard hints */}
      <div
        style={{
          position: "absolute",
          bottom: "16px",
          right: "24px",
          display: "flex",
          gap: "12px",
        }}
      >
        {[
          { key: "←→", label: "Navigate" },
          { key: "Space", label: "Next" },
          { key: "Esc", label: "Close" },
        ].map((hint) => (
          <div
            key={hint.key}
            style={{ display: "flex", alignItems: "center", gap: "4px" }}
          >
            <span
              style={{
                padding: "2px 6px",
                borderRadius: "4px",
                background: "rgba(255,255,255,0.1)",
                border: "1px solid rgba(255,255,255,0.15)",
                fontSize: "10px",
                color: "rgba(255,255,255,0.5)",
                fontWeight: 500,
                fontFamily: "monospace",
              }}
            >
              {hint.key}
            </span>
            <span style={{ fontSize: "10px", color: "rgba(255,255,255,0.35)" }}>
              {hint.label}
            </span>
          </div>
        ))}
      </div>
    </motion.div>
  );
};

export default PreviewPlayer;
